import { useEffect, useState, useCallback, useMemo } from "react";
import { Row, Col, Input, Label, Collapse, Button } from "reactstrap";
import useCartStore from "../../stores/useCartStore";
import { toDate, formatDate, dateAddYears } from "../../Utilities/dateUtils";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LicenseTypes } from "../../constants/LicenseTypes";
import DataGrid from "../DataGrids/DataGrid";

export default function ShopStudentsSelect({
  studentsOnCurrentProgram,
  lastAddedProduct,
  addOrRemoveStudentFromCartItem,
  allStudentsWithLicenses,
  allStudents,
  preselectedStudent,
  removeManyFromCart,
  addManyToCart,
  allChecked,
  setAllChecked,
}) {
  const [studentsForAdding, setStudentsForAdding] = useState([]);
  const [filteredStudentsForSelect, setFilteredStudentsForSelect] = useState(
    []
  );
  const [filterStudentsBy, setFilteredStudentsBy] = useState("");
  const [studentsWithCurrentLicense, setStudentsWithCurrentLicense] = useState(
    []
  );
  const [showStudentsWithLicense, setShowStudentsWithLicense] = useState(false);

  const columns = useMemo(
    () => [
      {
        id: "input",
        key: "input",
        header: "Välj",
        width: 100,
        sortable: false,
        cell: (props) => props.getValue(),
      },
      { id: "name", key: "name", header: "Namn", width: 300 },
      { id: "class", key: "class", header: "Klass", width: 300 },
      {
        id: "aplLicense",
        key: "aplLicense",
        header: "Apl-licens",
        width: 400,
        sortable: false,
        cell: (props) => props.getValue(),
      },
    ],
    [],
  );

  const [rowsForSelect, setRowsForSelect] = useState([]);
  const [rowsForNotSelect, setRowsForNotSelect] = useState([]);
  const allStudentsList = useMemo(
    () =>
      Array.isArray(allStudents)
        ? allStudents
        : Array.isArray(allStudents?.result)
          ? allStudents.result
          : [],
    [allStudents],
  );
  const allStudentsWithLicensesList = useMemo(
    () => allStudentsWithLicenses?.data ?? [],
    [allStudentsWithLicenses?.data],
  );

  const cartResult = useCartStore((s) => s.result);

  const checkStudentHasLicense = useCallback(
    (studentLicenses) => {
      if (!studentLicenses) {
        return false;
      }

      const lastAddedValidFrom = lastAddedProduct.isPlusPackage
        ? lastAddedProduct.plusPackage.validFrom
        : lastAddedProduct.validFrom;

      const lastAddedYears = lastAddedProduct.isPlusPackage
        ? lastAddedProduct.studentLicense.years
        : 3;

      if (studentLicenses.hasValidAplLicense) {
        if (
          toDate(studentLicenses.aplLicenseValidTo) >=
          toDate(lastAddedValidFrom)
        ) {
          return true;
        }
      } else if (studentLicenses.aplLicenseValidFrom) {
        let newStartDate = formatDate(lastAddedValidFrom, "yyyyMMdd");
        let newEndDate = formatDate(dateAddYears(lastAddedValidFrom, lastAddedYears), "yyyyMMdd");
        let existingStartDate = formatDate(
          studentLicenses.aplLicenseValidFrom, "yyyyMMdd"
        );
        let existingEnddate = formatDate(studentLicenses.aplLicenseValidTo,
          "yyyyMMdd"
        );
        if (newEndDate > existingStartDate && newStartDate < existingEnddate) {
          return true;
        }
      }

      return false;
    },
    [lastAddedProduct]
  );

  const createLicensesFallback = (licenses) =>
    licenses ?? {
      programLicenseIds: [],
      aplLicenseValidFrom: null,
      aplLicenseValidTo: null,
      hasValidAplLicense: false,
    };

  const studentsOnCurrentProgramList = useMemo(
    () =>
      studentsOnCurrentProgram.map((stud) => ({
        ...stud,
        licenses: createLicensesFallback(stud.licenses),
      })),
    [studentsOnCurrentProgram],
  );

  useEffect(() => {
    let studentsWithConflictingAplLicense = [];
    if (lastAddedProduct.isPlusPackage) {
      studentsOnCurrentProgramList.forEach((stud) => {
        if (checkStudentHasLicense(stud.licenses)) {
          studentsWithConflictingAplLicense.push(stud);
        }
      });
    }
    //If generic apl license selected show all students
    if (lastAddedProduct.type === LicenseTypes.APLOnly) {
      let tempStudentsForNotChoosing = [];
      let tempStudentsForChoosing = [];
      const tempAllStudents = [];
      allStudentsList.forEach((stud) => {
        const licenses = allStudentsWithLicensesList.find(
          (studWL) => studWL.studentId === stud.id,
        );
        let studentToAdd = {
          name: stud.name,
          id: stud.id,
          class: stud.class,
          licenses: createLicensesFallback(licenses),
        };
        tempAllStudents.push(studentToAdd);
      });
      tempAllStudents.forEach((stud) => {
        if (checkStudentHasLicense(stud.licenses)) {
          tempStudentsForNotChoosing.push({ ...stud, hasApl: true });
        } else {
          tempStudentsForChoosing.push({ ...stud, hasApl: false });
        }
      });
      setStudentsForAdding(tempStudentsForChoosing);
      setFilteredStudentsForSelect(tempStudentsForChoosing);
      setStudentsWithCurrentLicense(tempStudentsForNotChoosing);
      //If pluslicense Show only students on current program that doesnt have apl
    } else if (
      lastAddedProduct.type === LicenseTypes.PlusLicense ||
      lastAddedProduct.type === LicenseTypes.StudentLicencePlus
    ) {
      let tempStudentsForNotChoosing = [];
      let tempStudentsForChoosing = [];
      const tempAllStudents = [];
      allStudentsList.forEach((stud) => {
        const licenses = allStudentsWithLicensesList.find(
          (studWL) => studWL.studentId === stud.id,
        );
        let studentToAdd = {
          name: stud.name,
          id: stud.id,
          class: stud.class,
          licenses: createLicensesFallback(licenses),
        };
        tempAllStudents.push(studentToAdd);
      });
      tempAllStudents.forEach((stud) => {
        if (checkStudentHasLicense(stud.licenses)) {
          tempStudentsForNotChoosing.push({ ...stud, hasApl: true });
        } else {
          tempStudentsForChoosing.push({ ...stud, hasApl: false });
        }
      });
      tempStudentsForChoosing = tempStudentsForChoosing.filter((stud) =>
        stud.licenses.programLicenseIds.includes(
          lastAddedProduct.programLicenseId,
        ),
      );
      tempStudentsForChoosing = tempStudentsForChoosing.filter((stud) =>
        studentsOnCurrentProgramList.some(
          (progStud) => progStud.id === stud.id,
        ),
      );
      setStudentsForAdding(tempStudentsForChoosing);
      setFilteredStudentsForSelect(tempStudentsForChoosing);
      setStudentsWithCurrentLicense(tempStudentsForNotChoosing);
      //StudentLicens, visa alla elever som inte har elevlicens
    } else if (lastAddedProduct.type === LicenseTypes.StudentLicense) {
      let tempStudentsForChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          !stud.licenses.programLicenseIds.includes(
            lastAddedProduct.programLicenseId,
          ),
      );
      let tempStudentsForNotChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          stud.licenses.programLicenseIds.includes(
            lastAddedProduct.programLicenseId,
          ),
      );
      setStudentsWithCurrentLicense(tempStudentsForNotChoosing);
      setStudentsForAdding(tempStudentsForChoosing);
      setFilteredStudentsForSelect(tempStudentsForChoosing);
    } else if (lastAddedProduct.isPlusPackage) {
      //Visa alla elever som inte har licens och som inte har apl för perioden.
      let tempStudentsForChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          !stud.licenses.programLicenseIds.includes(
            lastAddedProduct.studentLicense.programLicenseId,
          ),
      );
      studentsWithConflictingAplLicense.forEach((studwithconflict) => {
        tempStudentsForChoosing = tempStudentsForChoosing.filter(
          (stud) => stud.id !== studwithconflict.id,
        );
      });

      let tempStudentsForNotChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          stud.licenses.programLicenseIds.includes(
            lastAddedProduct.studentLicense.programLicenseId,
          ),
      );
      studentsWithConflictingAplLicense.forEach((studWithconflicting) => {
        if (
          !tempStudentsForNotChoosing.some(
            (stud) => stud.id === studWithconflicting.id,
          )
        ) {
          tempStudentsForNotChoosing.push({
            ...studWithconflicting,
            hasApl: true,
          });
        } else {
          tempStudentsForNotChoosing.find(
            (stud) => stud.id === studWithconflicting.id,
          ).hasApl = true;
        }
      });
      setStudentsWithCurrentLicense(tempStudentsForNotChoosing);
      setStudentsForAdding(tempStudentsForChoosing);
      setFilteredStudentsForSelect(tempStudentsForChoosing);
    }

    if (preselectedStudent) {
      setStudentsForAdding(
        studentsOnCurrentProgramList.filter(
          (stud) => stud.id === preselectedStudent.id,
        ),
      );
      setFilteredStudentsForSelect(
        studentsOnCurrentProgramList.filter(
          (stud) => stud.id === preselectedStudent.id,
        ),
      );
    }
  }, [
    studentsOnCurrentProgram,
    lastAddedProduct,
    allStudents,
    allStudentsWithLicenses.data,
    allStudentsWithLicenses.isPending,
    preselectedStudent,
    checkStudentHasLicense,
    allStudentsWithLicensesList,
    studentsOnCurrentProgramList,
    allStudentsList,
  ]);

  useEffect(() => {
    if (filterStudentsBy.length > 0) {
      setFilteredStudentsForSelect(
        studentsForAdding.filter(
          (stud) =>
            stud.name.toLowerCase().includes(filterStudentsBy.toLowerCase()) ||
            stud.class.name
              .toLowerCase()
              .includes(filterStudentsBy.toLowerCase())
        )
      );
    } else {
      setFilteredStudentsForSelect(studentsForAdding);
    }
  }, [filterStudentsBy, studentsForAdding]);

  const handleToggleAllStudents = () => {
    if (allChecked) {
      removeManyFromCart(filteredStudentsForSelect);
      setAllChecked(false);
    } else {
      addManyToCart(filteredStudentsForSelect);
      setAllChecked(true);
    }
  };

  const handleGetCheckStatus = useCallback(
    (studentId) => {
      if (lastAddedProduct.isPlusPackage) {
        if (
          cartResult.some(
            (cartitem) =>
              cartitem.fortnoxNumber ===
                lastAddedProduct.studentLicense.fortnoxNumber &&
              cartitem.addedStudents.some(
                (addedstud) => addedstud.id === studentId
              )
          )
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (
          cartResult.some(
            (cartitem) =>
              cartitem.fortnoxNumber === lastAddedProduct.fortnoxNumber &&
              cartitem.addedStudents.some(
                (addedstud) => addedstud.id === studentId
              )
          )
        ) {
          return true;
        } else {
          return false;
        }
      }
    },
    [lastAddedProduct, cartResult]
  );

  useEffect(() => {
    if (lastAddedProduct) {
      let tempStudentsForSelect = [];
      filteredStudentsForSelect.forEach((stud, i) => {
        let tempStudentToAdd = {
          input: null,
          name: null,
          class: null,
          aplLicense: null,
        };
        tempStudentToAdd.name = stud.name;
        tempStudentToAdd.input = (
          <Input
            style={{
              marginLeft: "1em",
              marginRight: "1em",
              marginTop: "0",
              position: "relative",
              cursor:
                lastAddedProduct.type !== LicenseTypes.StudentLicense &&
                checkStudentHasLicense(stud.licenses)
                  ? "not-allowed"
                  : "cursor",
            }}
            type="checkbox"
            onChange={() => addOrRemoveStudentFromCartItem(stud)}
            checked={handleGetCheckStatus(stud.id)}
            disabled={
              lastAddedProduct.type !== LicenseTypes.StudentLicense &&
              checkStudentHasLicense(stud.licenses)
            }
          />
        );
        tempStudentToAdd.class = stud.class.name;
        tempStudentToAdd.aplLicense =
          lastAddedProduct.type === LicenseTypes.StudentLicense ? (
            ""
          ) : stud.licenses.aplLicenseValidTo !== null ? (
            stud.licenses.hasValidAplLicense ? (
              <>
                <FormattedMessage id="StudentHasAValidAplLicenseUntil" />{" "}
                {formatDate(stud.licenses.aplLicenseValidTo, "yyyy-MM-dd")}
              </>
            ) : (
              <FormattedMessage
                values={{
                  dateFrom: formatDate(stud.licenses.aplLicenseValidFrom,
                    "yyyy-MM-dd"
                  ),
                  dateUntil: formatDate(stud.licenses.aplLicenseValidTo,
                    "yyyy-MM-dd"
                  ),
                }}
                id="StudentHasAValidAplLicenseFromUntil"
              />
            )
          ) : null;
        tempStudentsForSelect.push(tempStudentToAdd);
      });
      setRowsForSelect(tempStudentsForSelect);
    }
  }, [
    filteredStudentsForSelect,
    addOrRemoveStudentFromCartItem,
    checkStudentHasLicense,
    lastAddedProduct,
    cartResult,
    handleGetCheckStatus,
  ]);

  useEffect(() => {
    let tempStudentsForNotSelect = [];
    studentsWithCurrentLicense.forEach((stud, i) => {
      let tempStudentToAdd = {
        input: null,
        name: null,
        class: null,
        aplLicense: null,
      };
      tempStudentToAdd.name = stud.name;
      tempStudentToAdd.input = (
        <Input
          style={{
            marginLeft: "1em",
            marginRight: "1em",
            marginTop: "0",
            position: "relative",
            cursor: "not-allowed",
          }}
          type="checkbox"
          checked={true}
          disabled={true}
        />
      );
      tempStudentToAdd.class = stud.class.name;
      tempStudentToAdd.aplLicense =
        lastAddedProduct.type === LicenseTypes.StudentLicense ? (
          ""
        ) : stud.licenses.aplLicenseValidTo !== null ? (
          stud.licenses.hasValidAplLicense ? (
            <>
              <FormattedMessage id="StudentHasAValidAplLicenseUntil" />{" "}
              {formatDate(stud.licenses.aplLicenseValidTo, "yyyy-MM-dd")}
            </>
          ) : (
            <FormattedMessage
              values={{
                dateFrom: formatDate(stud.licenses.aplLicenseValidFrom,
                  "yyyy-MM-dd"
                ),
                dateUntil: formatDate(stud.licenses.aplLicenseValidTo,
                  "yyyy-MM-dd"
                ),
              }}
              id="StudentHasAValidAplLicenseFromUntil"
            />
          )
        ) : null;
      tempStudentsForNotSelect.push(tempStudentToAdd);
    });
    setRowsForNotSelect(tempStudentsForNotSelect);
  }, [studentsWithCurrentLicense, lastAddedProduct.type]);

  return (
    <>
      <Row className="mb-2">
        <Col lg="4" md="4" sm="6" xs="12">
          <Input
            type="text"
            placeholder="Filtrera elever på namn eller klass..."
            onChange={(data) => setFilteredStudentsBy(data.target.value)}
          ></Input>
        </Col>
      </Row>
      <Row>
        <Col>
          <Label for="checkAll">
            <FormattedMessage id="SelectSlashUnselectAll" />
          </Label>
          <Input
            style={{ marginLeft: ".5em" }}
            name="checkAll"
            type="checkbox"
            onChange={() => handleToggleAllStudents()}
            checked={allChecked}
          />
        </Col>
      </Row>

      <DataGrid
        rows={rowsForSelect}
        columns={columns}
        initialSorting={[{ id: "name", desc: false }]}
        noDataMessage={<FormattedMessage id="NoStudentsToChoose" />}
      />

      <>
        <Row className="mt-4">
          <Col>
            <Button
              color="primary"
              outline
              onClick={() =>
                setShowStudentsWithLicense(!showStudentsWithLicense)
              }
              className="mb-3"
            >
              {showStudentsWithLicense ? (
                <FontAwesomeIcon className="me-2" icon="chevron-down" />
              ) : (
                <FontAwesomeIcon className="me-2" icon="chevron-right" />
              )}
              <FormattedMessage id="ShowStudentsWithLicense" />
            </Button>
          </Col>
        </Row>
        <Collapse isOpen={showStudentsWithLicense}>
          <DataGrid
            rows={rowsForNotSelect}
            columns={columns}
            initialSorting={[{ id: "name", desc: false }]}
            noDataMessage={<FormattedMessage id="NoStudents" />}
          />
        </Collapse>
      </>
    </>
  );
}
